import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import clsx from 'clsx'
import { ChevronDown } from 'lucide-react'
import * as React from 'react'

const DropdownMenu = DropdownMenuPrimitive.Root

function DropdownMenuTrigger({
  className,
  label,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger> & { label?: string }) {
  return (
    <DropdownMenuPrimitive.Trigger
      data-slot="dropdown-menu-trigger"
      className={clsx(
        'backgroundColorBlackToWhite border-gray-90 flex h-10 w-full items-center justify-between gap-2 rounded-xl border border-solid px-4 py-2 font-medium outline-0 transition-colors',
        'focus:border-gray-70 data-[state=open]:border-gray-70',
        'disabled:pointer-events-none disabled:opacity-50',
        '[&>span]:line-clamp-1 [&>span]:flex [&>span]:items-center [&>span]:gap-2',
        className
      )}
      {...props}
    >
      {label && <span className="text-gray-90 text-body-3 gap-0.5">{label}</span>}
      <div className="flex w-full items-center justify-between gap-2">
        {children}
        <ChevronDown className="text-gray-80 size-4 shrink-0 opacity-70 my-auto" />
      </div>
    </DropdownMenuPrimitive.Trigger>
  )
}

function DropdownMenuContent({
  className,
  side = 'bottom',
  sideOffset = 4,
  align = 'start',
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        side={side}
        sideOffset={sideOffset}
        align={align}
        className={clsx(
          'backgroundColorBlackToWhite text-gray-90 z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-xl border border-solid border-gray-100 shadow-lg',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
}

function DropdownMenuItem({ className, ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Item>) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      className={clsx(
        'text-gray-80 text-body-2 relative flex w-full cursor-default items-center rounded-lg py-2 pr-2 pl-4 outline-none select-none',
        'focus:bg-gray-100/80 data-disabled:pointer-events-none data-disabled:opacity-50 data-highlighted:bg-gray-100/80',
        className
      )}
      {...props}
    />
  )
}

function DropdownMenuSeparator({ className, ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={clsx('-mx-1 my-1 h-px border-t border-solid border-gray-100', className)}
      {...props}
    />
  )
}

function DropdownMenuLabel({ className, ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Label>) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      className={clsx('text-gray-80 text-body3-regular px-2 py-1.5 font-medium', className)}
      {...props}
    />
  )
}

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
}
